import React, { useEffect, useState } from 'react';
import { X, Sparkles } from 'lucide-react';

/*
 * PlanIntake — the ~3-question intake. Capture the essentials (what you're
 * planning, the date, rough guest count + what you need help with), then the
 * backend generates a tailored starter plan. Deterministic; no LLM required.
 */
export default function PlanIntake({ open, intent = null, settings = null, busy = false, onClose, onSubmit }) {
  const [form, setForm] = useState({ event_type_freetext: '', event_date: '', estimated_guests: '', needs_text: '' });

  useEffect(() => {
    if (!open) return;
    setForm({
      event_type_freetext: intent?.event_type_freetext || settings?.event_name || '',
      event_date: intent?.event_date ? String(intent.event_date).slice(0, 10)
        : (settings?.date && /^\d{4}-\d{2}-\d{2}/.test(settings.date) ? settings.date.slice(0, 10) : ''),
      estimated_guests: intent?.estimated_guests ? String(intent.estimated_guests) : '',
      needs_text: '',
    });
  }, [open, intent, settings]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    await onSubmit({
      event_type_freetext: form.event_type_freetext.trim() || null,
      event_date: form.event_date || null,
      estimated_guests: form.estimated_guests ? Number(form.estimated_guests) : null,
      needs_text: form.needs_text.trim() || null,
    });
  };

  return (
    <div className="ero-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <form className="ero-modal" onSubmit={submit} data-testid="plan-intake">
        <button type="button" className="ero-modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        <h3><Sparkles size={18} style={{ verticalAlign: '-3px', color: 'var(--ero-gold)' }} /> Set up your plan</h3>
        <p className="sub">Three quick things and I’ll build a tailored starter checklist — then keep it in sync with your live data.</p>

        <div className="ero-field">
          <label htmlFor="pi-type">What are you planning?</label>
          <input id="pi-type" type="text" value={form.event_type_freetext} onChange={set('event_type_freetext')}
                 placeholder="e.g. 80th birthday, wedding, anniversary…" autoFocus />
        </div>

        <div className="ero-field-row">
          <div className="ero-field">
            <label htmlFor="pi-date">Event date</label>
            <input id="pi-date" type="date" value={form.event_date} onChange={set('event_date')} />
          </div>
          <div className="ero-field">
            <label htmlFor="pi-guests">Approx. guests</label>
            <input id="pi-guests" type="number" min="0" value={form.estimated_guests} onChange={set('estimated_guests')}
                   placeholder="e.g. 130" />
          </div>
        </div>

        <div className="ero-field">
          <label htmlFor="pi-needs">What do you need help with? <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--ero-ink-4)' }}>(optional)</span></label>
          <textarea id="pi-needs" value={form.needs_text} onChange={set('needs_text')}
                    placeholder="e.g. I’ve got the venue but still need photography, catering, and favors." />
        </div>

        <div className="ero-modal-actions">
          <button type="button" className="ero-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="ero-btn primary" disabled={busy}>
            {busy ? 'Building…' : 'Build my plan'}
          </button>
        </div>
      </form>
    </div>
  );
}
